import { Injectable, inject, signal } from '@angular/core';
import { Observable, Subject, filter, map, take, timeout } from 'rxjs';
import {
  BRIDGE_WINDOW,
  BridgeEndpoint,
  EditorToHostMessage,
  EmbeddedAuthPayload,
  HostToEditorMessage,
  isEmbeddedEditor,
  parseHostToEditor,
  toOrigin,
} from '@infinitform/platform-core';
import { environment } from '../../config/editor-environment';

/** How long an embedded editor waits for the cabinet to hand over a token. */
export const EMBEDDED_AUTH_TIMEOUT_MS = 15_000;

type AuthMessage = Extract<HostToEditorMessage, { type: 'auth' }>;

/**
 * The editor's end of the bridge to the cloud cabinet. Inert unless the
 * editor runs in the cabinet's frame (`?embedded=1` and framed).
 */
@Injectable({ providedIn: 'root' })
export class EmbeddedBridgeService {
  private readonly win = inject(BRIDGE_WINDOW);
  private readonly hostOrigin = toOrigin(environment.FRONTEND_ADDRESS);

  readonly isEmbedded = this.hostOrigin !== null && isEmbeddedEditor(this.win);
  /** True while the cabinet keeps this editor in the background. */
  readonly hostPaused = signal(false);

  private readonly incoming = new Subject<HostToEditorMessage>();
  readonly messages$: Observable<HostToEditorMessage> = this.incoming.asObservable();

  private readonly endpoint: BridgeEndpoint<HostToEditorMessage, EditorToHostMessage> | null;
  private readonly dirtySources = new Set<string>();
  private reportedDirty = false;
  private reportedLoading: boolean | null = null;

  constructor() {
    if (this.isEmbedded && this.hostOrigin) {
      this.endpoint = new BridgeEndpoint<HostToEditorMessage, EditorToHostMessage>({
        listenOn: this.win,
        peer: () => this.win.parent,
        peerOrigin: this.hostOrigin,
        ownSource: 'infinitform-editor',
        parse: parseHostToEditor,
      });
      this.endpoint.start((message) => this.incoming.next(message));
    } else {
      this.endpoint = null;
    }
  }

  send(message: EditorToHostMessage): void {
    this.endpoint?.send(message);
  }

  /** The next token the cabinet sends; errors after `timeoutMs`. */
  nextAuth(timeoutMs: number): Observable<EmbeddedAuthPayload> {
    return this.messages$.pipe(
      filter((message): message is AuthMessage => message.type === 'auth'),
      map((message) => message.payload),
      take(1),
      timeout({ first: timeoutMs })
    );
  }

  /** Asks the cabinet for a token and waits for it. */
  requestAuth(timeoutMs: number): Observable<EmbeddedAuthPayload> {
    return new Observable<EmbeddedAuthPayload>((subscriber) => {
      const subscription = this.nextAuth(timeoutMs).subscribe(subscriber);
      this.send({ type: 'auth-request', payload: null });
      return subscription;
    });
  }

  /** Asks the cabinet to show one of its own pages instead of loading it inside the frame. */
  navigateHost(cabinetPath: string): void {
    this.send({ type: 'navigate', payload: { cabinetPath } });
  }

  /** Records unsaved work from one part of the editor and tells the cabinet when the overall state changes. */
  setDirty(source: string, value: boolean): void {
    if (value) {
      this.dirtySources.add(source);
    } else {
      this.dirtySources.delete(source);
    }
    const dirty = this.dirtySources.size > 0;
    if (dirty === this.reportedDirty) return;
    this.reportedDirty = dirty;
    this.send({ type: 'dirty', payload: { value: dirty } });
  }

  /** Tells the cabinet whether the project is still loading, once per change. */
  setLoading(value: boolean): void {
    if (!this.isEmbedded || value === this.reportedLoading) return;
    this.reportedLoading = value;
    this.send({ type: 'loading', payload: { value } });
  }
}
